import { Guid, EmptyGuid } from '@Shared.Angular/@types/guid';
import { getService } from '@Shared.Angular/flowingly.hooks/useService';
import { FileFolder } from '@Shared.Angular/flowingly.services/file.service';
import React, {
  useState,
  useRef,
  ChangeEvent,
  useEffect,
  useMemo
} from 'react';
import IconButton from '../buttons/IconButton';

interface FileUploadProps {
  maxFiles?: number;
  onUpload?: (fileId: Guid) => void;
  onFileSelected?: (file: File) => void;
  acceptTypes?: string[];
  title?: string;
  uploadFolder?: FileFolder;
  isDisabled?: boolean;
  onUploadStart?: () => void;
  onUploadComplete?: () => void;
}

export const FileUpload = ({
  maxFiles = 1,
  acceptTypes = ['*'],
  onUpload,
  onFileSelected,
  title,
  uploadFolder,
  isDisabled = false,
  onUploadStart,
  onUploadComplete
}: FileUploadProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileService = getService('fileService');
  const notificationService = getService('notificationService');
  const accept = acceptTypes.join(',');
  const onlyImages = acceptTypes.every((type) => type.startsWith('image/'));
  const fileTypePrefix = onlyImages ? 'an' : 'a';
  const fileTypeDescription = onlyImages ? 'image' : 'file';

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    handleFiles(selectedFiles);
  };

  const handleFiles = (selectedFiles: File[]) => {
    setError(null);

    if (selectedFiles.length + files.length > maxFiles) {
      setError(
        `You can only upload a maximum of ${maxFiles} ${fileTypeDescription}`
      );
      return;
    }

    setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  };

  const removeFile = (index: number) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    const isGenerationFile =
      uploadFolder === FileFolder.FlowModelGeneration ||
      uploadFolder === FileFolder.ProcedureGeneration;
    if (isGenerationFile) {
      if (files.length > 1) {
        notificationService.showErrorToast(
          'You can only upload one file at a time for generation.'
        );
        return;
      }
      try {
        onUploadStart && onUploadStart();
        const uploadedFile = await getService(
          'flowModelGenerationFileService'
        ).uploadFile(files[0], EmptyGuid);
        notificationService.showSuccessToast(files[0].name + ' uploaded.');
        onUpload && onUpload(uploadedFile.id);
      } catch (error) {
        setFiles([]);
        notificationService.showErrorToast(
          `Error uploading ${files[0].name}: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`
        );
      } finally {
        onUploadComplete && onUploadComplete();
      }
    } else {
      onUploadStart && onUploadStart();
      const promises = files.map(function (file) {
        return fileService.reduceImageFile(file, 1920, 1080).then(
          (resizedFile) => {
            return fileService
              .uploadFileToFolder(resizedFile, uploadFolder)
              .then(
                function (response) {
                  if (response.valid) {
                    const fileId = response.data;

                    notificationService.showSuccessToast(
                      file.name + ' uploaded.'
                    );
                    onUpload && onUpload(fileId);
                  }
                },
                function () {
                  notificationService.showErrorToast(
                    'Error uploading ' + file.name
                  );
                }
              );
          },
          () => {
            notificationService.showWarningToast(
              file.name + ' is not an accepted file type.'
            );
          }
        );
      });

      Promise.all(promises)
        .then(() => {
          setFiles([]);
        })
        .finally(() => {
          onUploadComplete && onUploadComplete();
        });
    }
  };

  useEffect(() => {
    if (maxFiles === 1 && files.length > 0) {
      if (onFileSelected) {
        onFileSelected(files[0]);
      } else {
        uploadFiles();
      }
    }
  }, [files]);

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const acceptedFileTypes = useMemo(() => {
    return acceptTypes
      .filter((type) => type && type.startsWith('.'))
      .sort()
      .join(', ')
      .replaceAll('.', '')
      .toUpperCase();
  }, [acceptTypes]);

  return (
    <div className="file-upload">
      {title && <h5 className="file-upload__title">{title}</h5>}
      {files.length < maxFiles && (
        <div
          className={`file-upload__dropzone ${
            isDragging ? 'file-upload__dropzone--dragging' : ''
          } ${isDisabled ? 'disabled' : ''}`}
          onDragOver={isDisabled ? undefined : handleDragOver}
          onDragLeave={isDisabled ? undefined : handleDragLeave}
          onDrop={isDisabled ? undefined : handleDrop}
          onClick={isDisabled ? undefined : triggerFileInput}
          role="button"
          data-testid="file-upload-dropzone"
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
            multiple={maxFiles > 1}
            accept={accept}
            data-testid="file-upload-input"
          />
          <i className="fa-light fa-cloud-arrow-up"></i>
          <div>
            Drag and drop {fileTypePrefix} {fileTypeDescription} here
          </div>
          <div>
            or{' '}
            <span className="file-upload__dropzone__browse">
              browse your device{' '}
            </span>
            {acceptedFileTypes && (
              <span className="file-upload__dropzone__types">
                • {acceptedFileTypes}
              </span>
            )}
          </div>
          {maxFiles > 1 && (
            <p className="file-upload__info">{`Maximum ${maxFiles} ${fileTypeDescription}`}</p>
          )}
        </div>
      )}

      {error && <div className="file-upload__error">{error}</div>}

      {files.length > 0 && (
        <div className="file-upload__files">
          <ul>
            {files.map((file, index) => (
              <li key={index} className="file-upload__file">
                <span className="file-upload__file-name">{file.name}</span>
                <span className="file-upload__file-size">
                  ({(file.size / 1024).toFixed(2)} KB)
                </span>
                <IconButton
                  iconClass="fa-xmark"
                  title="Remove file"
                  testId={`remove-file-button-${index}`}
                  classNames="file-upload__file-remove"
                  onClick={() => removeFile(index)}
                  disabled={isDisabled}
                ></IconButton>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
